import React from 'react';
import { Slider, Box, Typography, Button, Loader } from '@lucid/lego';
import type { ExportHistoryLog } from '../../../constants';
import { formatDate } from '../../../../../utils/tools';
import styles from './ExportHistory.module.scss';

type ExportHistoryProps = {
  dataTestId?: string;
  isOpen: boolean;
  onClose: () => void;
  labels: Record<string, string>;
  theme: 'light' | 'dark';
  logs?: ExportHistoryLog[];
  isLoading?: boolean;
  isDownloading?: boolean;
  downloadingFileId?: string | null;
  onDownload: (fileId: string) => void;
  onExportAll: () => void;
  isExportAllPending?: boolean;
};

/**
 * Export History side slider for Prospect table.
 * @component
 * @param props - Component props
 * @param props.isOpen - Controls slider visibility
 * @param props.onClose - Handler for closing slider
 * @param props.labels - i18n labels map
 * @param props.theme - Current page theme
 * @param props.logs - Optional override for history logs
 * @param props.isLoading - Whether export history data is loading
 * @param props.isDownloading - Whether a history download is in progress
 * @param props.downloadingFileId - File id currently being downloaded
 * @param props.onDownload - Trigger download for a history item by file id
 * @param props.onExportAll - Trigger export all action
 * @param props.isExportAllPending - Whether export action is currently pending
 * @returns JSX.Element
 * @example
 * ```tsx
 * <ExportHistory
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   labels={labels}
 *   theme="dark"
 *   onDownload={handleDownload}
 *   onExportAll={handleExportAll}
 * />
 * ```
 */
const ExportHistory: React.FC<ExportHistoryProps> = ({
  dataTestId = 'export-history',
  isOpen,
  onClose,
  labels,
  theme,
  logs = [],
  isLoading = false,
  isDownloading = false,
  downloadingFileId = null,
  onDownload,
  onExportAll,
  isExportAllPending = false,
}) => {
  const sliderTheme = theme === 'dark' ? 'light' : 'dark';

  return (
    <Slider
      isOpen={isOpen}
      onClose={onClose}
      title={labels?.['table.exportHistory'] ?? 'Export History'}
      width="520px"
      direction="right"
      theme={sliderTheme}
    >
      {isOpen && (
        <Box className={styles.content} data-testid={dataTestId}>
          <Box className={styles.topBar}>
            <Typography variant="subtitle" size="medium" theme={sliderTheme} color="gray-4">
              {labels?.['table.activity'] ?? 'Activity'}
            </Typography>
            <Button
              type="secondary"
              color="neutral"
              size="small"
              theme={sliderTheme}
              label={labels?.['table.exportAll'] ?? 'Export All'}
              iconLeft={{ category: 'global', name: 'download' }}
              handleClick={onExportAll}
              disabled={isExportAllPending}
              gaComponent="prospects-history-export-all"
              dataTestId="history-export-all-button"
            />
          </Box>

          {isLoading ? (
            <Box className={styles.loaderContainer} data-testid="history-loader">
              <Loader size={32} theme={sliderTheme} />
            </Box>
          ) : (
            <Box className={styles.list}>
              {logs.map((log) => {
                const isCurrentDownloading = isDownloading && downloadingFileId === log.id;

                return (
                  <Box
                    key={log.id}
                    className={styles.row}
                    data-testid={`history-log-row-${log.id}`}
                  >
                    <Box className={styles.metaBlock}>
                      <Typography variant="label" size="small" theme={sliderTheme} color="gray-6">
                        {labels?.['table.dateAndTime'] ?? 'DATE AND TIME'}
                      </Typography>
                      <Typography variant="body" size="small" theme={sliderTheme} color="gray-3">
                        {formatDate(log.exportedAt)}
                      </Typography>
                    </Box>
                    <Box className={styles.metaBlock}>
                      <Typography variant="label" size="small" theme={sliderTheme} color="gray-6">
                        {labels?.['table.exporterName'] ?? 'NAME OF EXPORTER'}
                      </Typography>
                      <Typography variant="body" size="small" theme={sliderTheme} color="gray-3">
                        {log.exporterName}
                      </Typography>
                    </Box>
                    <Button
                      type="secondary"
                      color="neutral"
                      size="small"
                      theme={sliderTheme}
                      label={
                        isCurrentDownloading
                          ? (labels?.['table.downloading'] ?? 'DOWNLOADING...')
                          : (labels?.['table.download'] ?? 'Download')
                      }
                      handleClick={() => onDownload(log.id)}
                      disabled={isCurrentDownloading}
                      gaComponent="prospects-history-download"
                      dataTestId={`history-log-download-${log.id}`}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Slider>
  );
};

ExportHistory.displayName = 'ExportHistory';

export default ExportHistory;
